import { supabase } from '@/integrations/supabase/client';

// Lets the Masira Meeting Recorder browser extension (extension/) obtain a
// short-lived access token from an already-logged-in Masira tab, without
// the extension needing its own separate login. Only the current access
// token is ever exposed — never the refresh token, and never anything if
// there's no active session. Scoped to same-window messages
// (event.source === window, which only genuine same-page script — i.e. the
// extension's content script's injected postMessage call — can produce)
// and responds only to this window's own origin, never '*'.
const BRIDGE_REQUEST_TYPE = 'masira-extension-auth-request';
const BRIDGE_RESPONSE_TYPE = 'masira-extension-auth-response';

// Lets the extension ask an already-open, logged-in Masira tab to navigate
// itself to a specific view (e.g. Meeting Hub right after a successful
// submission) — fire-and-forget, no response expected. Resolves the
// tenant slug via the auth_tenant_id() RPC + a tenants lookup rather than
// reading it from the current URL: the tab may well be sitting on the
// bare public landing page ("/") rather than an already-tenant-scoped
// route — the auth-token bridge above works fine there (it's just
// localStorage, path-independent), but there's no slug in that URL to
// scrape, so URL-scraping silently no-ops. Same reliable-resolution
// pattern already used by MicrosoftConsentCallback.tsx for the equivalent
// problem. Index.tsx reads the resulting ?view= query param once on mount
// to pick the initial view.
const NAVIGATE_REQUEST_TYPE = 'masira-extension-navigate-request';

export function initExtensionBridge(): void {
  window.addEventListener('message', async (event: MessageEvent) => {
    if (event.source !== window) return;

    if (event.data?.type === BRIDGE_REQUEST_TYPE) {
      const { data: { session } } = await supabase.auth.getSession();
      window.postMessage(
        { type: BRIDGE_RESPONSE_TYPE, accessToken: session?.access_token ?? null },
        window.location.origin,
      );
      return;
    }

    if (event.data?.type === NAVIGATE_REQUEST_TYPE) {
      console.log('[extensionBridge] received navigate request', event.data);
      const view = typeof event.data.view === 'string' ? event.data.view : null;
      if (!view) return;

      const { data: tenantId, error: tenantIdError } = await supabase.rpc('auth_tenant_id');
      console.log('[extensionBridge] auth_tenant_id() ->', tenantId, tenantIdError);
      if (!tenantId) return;

      const { data: tenant, error: tenantError } = await supabase
        .from('tenants')
        .select('slug')
        .eq('id', tenantId)
        .maybeSingle();
      console.log('[extensionBridge] tenants lookup ->', tenant, tenantError);
      if (!tenant?.slug) return;

      const target = `/${tenant.slug}/app?view=${encodeURIComponent(view)}`;
      console.log('[extensionBridge] navigating to', target);
      window.location.href = target;
    }
  });
}
